import './README.css';

class README {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.form = null;
    this.emailInput = null;
    this.submitButton = null;
    this.errorMessage = null;
    this.successMessage = null;
    this.generalErrorMessage = null;
    this.isSubmitting = false;
    
    // Bind methods
    this.render = this.render.bind(this);
    this.setupEventListeners = this.setupEventListeners.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
    this.showSuccess = this.showSuccess.bind(this);
    this.showError = this.showError.bind(this);
  }
  
  /**
   * Render the README component
   */
  render() {
    // Add container class
    this.container.className = 'newsletter-container';
    
    this.container.innerHTML = `
      <h2 class="newsletter-title">Join our community and newsletter – We're Not Sorry ⚡️</h2>
      <p class="newsletter-subtitle">Inbox‑worthy drops from Our Appologies.</p>
      <p class="newsletter-description">Radically concise, spam-free, a bit unhinged, and always useful. Weekly at most.</p>
      
      <ul class="newsletter-list">
        <li>Meet other builders/creators/makers/doers/enthusiasts</li>
        <li>Get insider hot-takes on AI, apps, the creator economy, vertical markets, y más.</li>
        <li>See harrowing build-diaries, meme-rants, and behind‑the‑scenes vids.</li>
        <li>Be first in line for every "did‑they‑really?" release, discount, or beta group.</li>
        <li>Opt out anytime–although we may roast you for it.</li>
      </ul>
      
      <p class="newsletter-message typing-text"><span>Ready to join? Enter your email below.</span></p>
      
      <form class="neon-terminal-form">
        <div class="terminal-header">
          <span class="terminal-prompt">root@ourappologies:~$</span>
          <span class="terminal-command">subscribe.sh</span>
          <span class="blink">_</span>
        </div>
        
        <div class="input-container">
          <label for="email" class="terminal-prompt">Enter your email:</label>
          <div class="email-wrapper">
            <input type="email" id="email" class="terminal-input" placeholder="you@example.com" required />
          </div>
          <div class="form-error">Please enter a valid email address.</div>
        </div>
        
        <button type="submit" class="glitch-button">
          <span class="button-text">COUNT ME IN</span>
        </button>
        
        <div class="terminal-footer">
          <span class="green-text">// We guard your email address like prod keys.</span>
        </div>
        
        <div class="form-success">
          <div class="ascii-checkmark">
Thanks for vibing with us and welcome to the community!
          </div>
          <p>You'll receive an invite from Substack soon :)</p>
        </div>

        <div class="form-error-general">
          <p>Something went wrong. Please try again or email us directly at <a href="mailto:vibecheck@ourappologies.com">vibecheck@ourappologies.com</a></p>
        </div>
      </form>
    `;
    
    // Cache DOM elements
    this.form = this.container.querySelector('.neon-terminal-form');
    this.emailInput = this.container.querySelector('#email');
    this.submitButton = this.container.querySelector('.glitch-button');
    this.errorMessage = this.container.querySelector('.form-error');
    this.successMessage = this.container.querySelector('.form-success');
    this.generalErrorMessage = this.container.querySelector('.form-error-general');
    
    // Set up event listeners
    this.setupEventListeners();
    
    return this;
  }
  
  /**
   * Set up event listeners
   */
  setupEventListeners() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }
    
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => {
        // Hide error messages when user starts typing
        this.errorMessage.style.display = 'none';
        this.hideGeneralError();
      });
    }
  }
  
  /**
   * Handle form submission with Google Apps Script integration
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    event.preventDefault();
    
    if (this.isSubmitting) return; // Prevent double submission
    
    const email = this.emailInput.value.trim();
    
    // Reset error states
    this.hideAllErrors();
    
    if (!this.validateEmail(email)) {
      this.showError();
      return;
    }
    
    // Start submission
    this.isSubmitting = true;
    this.updateSubmitButton('PROCESSING...');
    
    try {
      const formData = {
        email,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        source: 'ourappologies-newsletter-form',
        status: 'Pending'
      };
      
      const response = await this.submitToGoogleAppsScript(formData);
      
      if (response.success) {
        this.showSuccess();
        this.form.reset();
      } else {
        throw new Error(response.error || 'Subscription failed');
      }
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      this.showGeneralError();
    } finally {
      this.isSubmitting = false;
      this.updateSubmitButton('COUNT ME IN');
    }
  }

  /**
   * Submit form data to Google Apps Script
   * @param {Object} formData - Form data to submit
   * @returns {Promise<Object>} - Response from Apps Script
   */
  async submitToGoogleAppsScript(formData) {
    const endpoint = import.meta.env.VITE_NEWSLETTER_ENDPOINT || 'APPS_SCRIPT_URL_PLACEHOLDER';
    
    // Debug logging for development
    console.log('🔧 Newsletter Environment check:');
    console.log('- VITE_NEWSLETTER_ENDPOINT:', import.meta.env.VITE_NEWSLETTER_ENDPOINT);
    console.log('- Using endpoint:', endpoint);
    console.log('- Mode:', import.meta.env.MODE);
    
    if (endpoint === 'APPS_SCRIPT_URL_PLACEHOLDER') {
      // Development mode - simulate submission
      console.log('Development mode - simulating newsletter submission:', formData);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, message: 'Development newsletter subscription successful' });
        }, 1500);
      });
    }
    
    console.log('🚀 Submitting newsletter to Google Apps Script:', endpoint);
    
    // Use text/plain to avoid CORS preflight
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('📨 Newsletter response from Apps Script:', result);
    return result;
  }

  /**
   * Update submit button text and state
   * @param {string} text - Button text to display
   */
  updateSubmitButton(text) {
    if (this.submitButton) {
      this.submitButton.disabled = this.isSubmitting;
      this.submitButton.innerHTML = `<span class="button-text">${text}</span>`;
    }
  }

  /**
   * Hide all error messages
   */
  hideAllErrors() {
    this.errorMessage.style.display = 'none';
    this.hideGeneralError();
  }

  /**
   * Hide general error message
   */
  hideGeneralError() {
    if (this.generalErrorMessage) {
      this.generalErrorMessage.style.display = 'none';
    }
  }

  /**
   * Show general error message
   */
  showGeneralError() {
    if (this.generalErrorMessage) {
      this.generalErrorMessage.style.display = 'block';
    }
  }
  
  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - Whether email is valid
   */
  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }
  
  /**
   * Show success message
   */
  showSuccess() {
    // Hide the form elements
    this.form.querySelectorAll(':not(.form-success)').forEach(el => {
      if (!el.classList.contains('form-success') && el.parentNode === this.form) {
        el.style.display = 'none';
      }
    });
    
    // Show success message
    this.successMessage.style.display = 'block';
    
    // Add typing animation class
    const message = this.successMessage.querySelector('p');
    if (message) {
      message.classList.add('typing-text');
      message.innerHTML = `<span>${message.textContent}</span>`;
    }
  }
  
  /**
   * Show error message
   */
  showError() {
    this.errorMessage.style.display = 'block';
    this.emailInput.focus();
  }
}

export default README; 